import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapStopOverlayProps } from "../types";
import { EnhancedRouteItem } from "../../../../components/Planner/types";
import { Inline } from "@design-system";
import {
  HiOutlineX,
  HiOutlineLocationMarker,
  HiOutlineClock,
  HiOutlineInformationCircle,
  HiOutlineCube,
  HiOutlineScale,
  HiOutlineExternalLink,
} from "react-icons/hi";
import { Timestamp } from "../../../../types";
import { formatStopTimingLabel } from "../../../../helpers/routeMappers";
import moment from "moment";
import { Button } from "@components";

export const MapStopOverlay: FC<MapStopOverlayProps> = ({
  selectedRoute,
  selectedStop,
  onClose,
}) => {
  const { t } = useTranslation(["common", "planner", "orders"]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (selectedStop) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [selectedStop]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match transition duration
  };

  if (!selectedRoute || !selectedStop) return null;

  // Find the matching stop from the route to get the cargo data
  const stopWithCargo = selectedRoute.route.find(
    (stop) => stop.location.id === selectedStop.location.id,
  ) as EnhancedRouteItem | undefined;

  const enhancedStop: EnhancedRouteItem = {
    ...selectedStop,
    isAvailable: false,
    cargos: stopWithCargo?.cargos || [],
    orderDisplayName: stopWithCargo?.orderDisplayName || "",
    stopDisplayName: stopWithCargo?.stopDisplayName || "",
  };

  // Theme colors based on pickup/delivery status
  const isPickup = enhancedStop.isPickup;
  const themeColors = isPickup
    ? {
        headerGradient:
          "bg-gradient-to-r from-lgb-primary to-lgb-primary-lighten",
        accent: "text-lgb-primary",
        background: "bg-brand-subtle dark:bg-lgb-blue-900/10",
        badge: "bg-pickup-muted text-pickup",
      }
    : {
        headerGradient:
          "bg-gradient-to-r from-lgb-main-green to-lgb-main-green-lighten",
        accent: "text-lgb-main-green",
        background: "bg-lgb-green-50 dark:bg-lgb-green-900/10",
        badge: "bg-delivery-muted text-delivery",
      };

  // Format date and time for display
  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return "";

    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Timestamp) {
        date = Timestamp.toMoment(timestamp).toDate();
      } else {
        return "";
      }

      return moment(date).format("DD.MM.YYYY");
    } catch (error) {
      return "";
    }
  };

  // Get human-readable timing description similar to RouteSelection
  const getTimingText = (timing?: {
    before?: string;
    after?: string;
    earliest: boolean;
    latest: boolean;
  }) => formatStopTimingLabel(timing, t, { variant: "orders-map" }) || "";
  // Format location address for URL
  const formatLocationForUrl = (): string => {
    if (!enhancedStop.location) return "";

    const addressLine = enhancedStop.location.addressLine || "";
    const postCode = enhancedStop.location.postCode || "";
    const city = enhancedStop.location.city || "";

    if (!addressLine || !postCode || !city) return "";

    const formattedAddress = addressLine
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");

    const formattedCity = city
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");

    return `${formattedAddress}-${postCode}-${formattedCity}`;
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div
        className={`bg-white dark:bg-layout rounded-lg shadow-xl max-w-md w-full overflow-hidden transition-all duration-300 transform ${
          isVisible ? "scale-100" : "scale-95"
        }`}
      >
        {/* Header with themed gradient background */}
        <div className="relative">
          <div
            className={`absolute top-0 left-0 right-0 h-14 ${themeColors.background} rounded-t-lg z-0`}
          ></div>

          <div
            className={`relative z-10 ${themeColors.headerGradient} p-3 flex justify-between items-center`}
          >
            <div className="flex items-center">
              <HiOutlineLocationMarker className="h-5 w-5 text-white mr-2" />
              <h3 className="text-base font-semibold text-white">
                {isPickup
                  ? t("planner:route_planner.pickup")
                  : t("planner:route_planner.delivery")}
              </h3>
              {!enhancedStop.isAvailable && (
                <div className="ml-2 flex items-center justify-center w-6 h-6 rounded-full bg-white/20 font-medium text-sm text-white">
                  {enhancedStop.stopNumber}
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-100 transition-colors"
            >
              <HiOutlineX className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 dark:bg-gray-900 space-y-4">
          {/* Location info */}
          <div className="flex items-start gap-3">
            <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-lg">
              <HiOutlineLocationMarker
                className={`h-4 w-4 ${themeColors.accent} dark:text-muted`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-text text-sm">
                {enhancedStop.stopDisplayName ||
                  enhancedStop.location.displayName}
              </h4>
              <p className="text-sm text-muted mt-0.5">
                {enhancedStop.location.addressLine}
              </p>
            </div>
          </div>

          {/* Time info */}
          <div className="flex items-start gap-3">
            <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-lg">
              <HiOutlineClock
                className={`h-4 w-4 ${themeColors.accent} dark:text-muted`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-text text-sm">
                {t("planner:route_planner.timing_label", "Tidsfrist")}
              </h4>
              <p className="text-sm text-muted mt-0.5">
                {formatDateTime(enhancedStop.stopDate)}{" "}
                {getTimingText(enhancedStop.timing)}
              </p>
            </div>
          </div>

          {/* Customer info */}
          <div className="flex items-start gap-3">
            <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-lg">
              <HiOutlineInformationCircle
                className={`h-4 w-4 ${themeColors.accent} dark:text-muted`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-text text-sm">
                {t("common:customer", "Kunde")}
              </h4>
              <p className="text-sm text-muted mt-0.5">
                {enhancedStop.orderDisplayName ||
                  t("common:no_customer_info", "Ingen kundeinfo")}
              </p>
            </div>
          </div>

          {/* Compact Cargo Section */}
          {enhancedStop.cargos && enhancedStop.cargos.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
              <Inline gap={2} className="mb-3">
                <HiOutlineCube className={`h-4 w-4 ${themeColors.accent}`} />
                <h4 className="font-medium text-text text-sm">
                  {t("common:cargo", "Last")} {t("common:details", "Detaljer")}
                </h4>
              </Inline>

              {/* Individual Cargo Items */}
              <div className="space-y-2">
                {enhancedStop.cargos.map((cargo, index) => {
                  const quantity = cargo.quantity || 0;
                  // Quantity-weighted totals, matching the canonical getCargoTotals
                  // calculation (dimensions in cm converted to m³).
                  const cargoWeight = Math.round(
                    (cargo.weight || 0) * quantity,
                  );
                  const cargoVolume =
                    Math.round(
                      (((cargo.length || 0) *
                        (cargo.width || 0) *
                        (cargo.height || 0)) /
                        1000000) *
                        quantity *
                        100,
                    ) / 100;

                  return (
                  <div
                    key={cargo.id || index}
                    className="bg-surface-muted p-3 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-sm font-medium text-text">
                          {cargo.descriptionShort ||
                            cargo.description ||
                            t("common:item", "Vare")}
                        </p>
                        <Inline gap={3} className="mt-1 text-sm text-muted">
                          <span>
                            {isPickup
                              ? t("common:pickup", "Hentes")
                              : t("common:delivery", "Leveres")}
                          </span>
                          <Inline gap={1}>
                            <HiOutlineScale className="h-3 w-3" />
                            <span>{cargoWeight} kg</span>
                          </Inline>
                          <Inline gap={1}>
                            <HiOutlineCube className="h-3 w-3" />
                            <span>{cargoVolume} m³</span>
                          </Inline>
                        </Inline>
                      </div>
                      <div
                        className={`${themeColors.badge} text-sm font-medium px-2 py-1 rounded-full whitespace-nowrap`}
                      >
                        {cargo.quantity} {t("common:pcs", "stk")}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-surface-muted p-3 flex justify-between gap-2">
          <Button
            onClick={() => {
              const locationParam = formatLocationForUrl();
              const url = locationParam
                ? `/resources/locations?location=${locationParam}`
                : `/resources/locations?location=idrettsvegen-155-5353-straume`;

              window.open(url, "_blank");
            }}
            variant="outline"
            size="sm"
            icon={<HiOutlineExternalLink className="h-4 w-4" />}
            iconPosition="left"
          >
            {t("common:view_location", "Vis lokasjon")}
          </Button>

          <Button onClick={handleClose} variant="secondary" size="sm">
            {t("common:close", "Lukk")}
          </Button>
        </div>
      </div>
    </div>
  );
};
